import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { geocodeByAddress, getLatLng } from "react-google-places-autocomplete";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { MapPin, Phone, Ambulance, HelpCircle, Navigation, Check, AlertTriangle, Loader, CheckCircle } from "lucide-react";
import { Steps } from "./Steps";
import { LocationStep } from "./steps/LocationStep";
import { DestinationStep } from "./steps/DestinationStep";
import { VehicleStep } from "./steps/VehicleStep";
import { SummaryStep } from "./steps/SummaryStep";
import { 
  Container,
  Grid,
  FormSection,
  MapSection
 } from "./StyledComponents";
import { animations } from "./animations";
import { SEVERITY_LEVELS, calculateSeverity } from "./utils";
import MapContainer from "../VehicleType/MapContainer";
import { QuestionsStep } from "./steps/QuestionsStep";


const AmbulanceFlow = ({ socket }) => {
  // State management
  const [step, setStep] = useState(1);
  const [startPoint, setStartPoint] = useState("Addis Ababa, Ethiopia");
  const startPlaceLocation = useRef({});
  const [locationError, setLocationError] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [isEnglishSpeaker, setIsEnglishSpeaker] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [destination, setDestination] = useState("Addis Ababa, Ethiopia");
  const [destinationLocation, setDestinationLocation] = useState(null);
  const [destinationError, setDestinationError] = useState("");
  const [suggestedDestination, setSuggestedDestination] = useState(null);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [alternatives, setAlternatives] = useState([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState(null);
  const [selectedVehicleTypeUUId, setSelectedVehicleTypeUUID] = useState(null);

  const [recommendedVehicle, setRecommendedVehicle] = useState(null);
  const [requestId, setRequestId] = useState([]);
  const [calculatedSeverity, setCalculatedSeverity] = useState(null);
  const [finalLinks, setFinalLinks] = useState(null);
  const [driverStatus, setDriverStatus] = useState("LOADING");
  const [acceptedDrivers, setAcceptedDrivers] = useState([]);
  const [mapKey, setMapKey] = useState(process.env.GOOGLE_MAP_API_KEY);
  const [isLoading, setIsLoading] = useState(false);

  const [emergencyRequestData, setEmergencyRequestData] = useState(null);
  const [noResults, setNoResults] = useState(null);
  const [retryCount, setRetryCount] = useState(0);


  // Socket listeners
  useEffect(() => {
   
    socket.on("ambulanceSubmitResponse", handleAmbulanceSubmitResponse);
    socket.on("emergencyResponse", handleDestinationConfirmResponse);
    socket.on("emergencyTripStarted", (data) => {
     console.log("=========Emergency trip started======:", data);
    });
    socket.on("crewMemberJoined", (data) => {
      console.log("=========Crew member joined======:", data);
    });
    socket.on("noResults", (data) => {
      setNoResults(data);
      setIsLoading(false);
      setDriverStatus("NONE");
    });
   

    return () => {
      socket.off("ambulanceSubmitResponse");
      socket.off("emergencyResponse");
      socket.off("noResults");
    };
  }, [socket]);

  // Initial data fetch
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      console.log("Fetching questions from /questions endpoint...");
      
      const questionsRes = await axios.get("/questions");
      console.log("Questions API response:", questionsRes);
      console.log("Questions data:", questionsRes.data);
      
      if (questionsRes.data && Array.isArray(questionsRes.data)) {
        setQuestions(questionsRes.data);
        console.log("Successfully set questions:", questionsRes.data.length, "questions loaded");
      } else {
        console.error("Invalid questions data format:", questionsRes.data);
      }
      
    } catch (error) {
      console.error("Error fetching questions:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handlers
  const handleAmbulanceSubmitResponse = (data) => {
    if (data.acceptedDrivers?.length > 0) {
      setAcceptedDrivers(data.acceptedDrivers);
      setDriverStatus("FOUND");
    } else {
      setDriverStatus("NONE");
    }
  };

  const handleDestinationConfirmResponse = (data) => {
    setRequestId(data.requestIds);
    console.log("=======response=====",data);
    
    // Use alternatives instead of availableVehicles
    if (data.alternatives && data.alternatives.length > 0) {
      setAlternatives(data.alternatives);
      // Keep vehicleTypes for backward compatibility if needed
      setVehicleTypes(data.availableVehicles || []);
    } else if (data.availableVehicles && data.availableVehicles.length > 0) {
      // Fallback to availableVehicles if alternatives not present
      setVehicleTypes(data.availableVehicles);
    }
    
    
    // Ensure recommendedVehicle is set properly
    if (data.recommendedVehicles && data.recommendedVehicles.length > 0) {
      setRecommendedVehicle(data.recommendedVehicles[0]);
      setSelectedVehicleType(data.recommendedVehicles[0]?.id);
      setSelectedVehicleTypeUUID(data.recommendedVehicles[0]?.uuid);
      
    }
    
    // Only move to next step after state updates
    setIsLoading(false);
    setTimeout(() => setStep(4), 100);
  };

  const handleStartPointChange = async (value) => {
    setLocationError("");
    setStartPoint(value.label);
    try {
      const results = await geocodeByAddress(value.label);
      const latLng = await getLatLng(results[0]);
      startPlaceLocation.current = latLng;
      setMapKey(prev => prev + 1);
    } catch (err) {
      console.error("Geocoding error:", err);
      setLocationError("Failed to locate address");
    }
  };

  const handlePhoneChange = (value) => {
    setPhone(value);
    setPhoneError("");
  };

  const handleEnglishSpeakerChange = (value) => {
    setIsEnglishSpeaker(value);
  };

  const validatePhone = () => {
    if (!phone) {
      setPhoneError("Phone is required");
      return false;
    }
    if (!phone.startsWith("251") && !phone.startsWith("09") && !phone.startsWith("07")) {
      setPhoneError("Invalid phone number format");
      return false;
    }
    if (phone.startsWith("251") && phone.length !== 12) {
      setPhoneError("Invalid phone number length");
      return false;
    }
    if ((phone.startsWith("09") || phone.startsWith("07")) && phone.length !== 10) {
      setPhoneError("Invalid phone number length");
      return false;
    }
    return true;
  };

  const handleNextFromLocation = () => {
    if (!startPoint) {
      setLocationError("Location is required");
      return;
    }
    if (!validatePhone()) {
      return;
    }
    setStep(2);
  };


  const getCriticalQuestionIds = questions => {
    const awakeQ = questions.find(q => /awake/i.test(q.text));
    const breathingQ = questions.find(q => /breath/i.test(q.text));
    if (!awakeQ || !breathingQ) {
      const yesNo = questions.filter(q => q.type !== 101).slice(0, 2);
      return yesNo.map(q => q.question_id);
    }
    return [awakeQ.question_id, breathingQ.question_id];
  };
  const criticalIds = React.useMemo(
    () => getCriticalQuestionIds(questions),
    [questions]
  );

  const handleOptionChange = (questionId, optionId, severity, questionType) => {
    // Find the selected option to get its link
    const question = questions.find(q => q.question_id === questionId);
    const option = question?.question_options?.find(opt => opt.option_id === optionId);
    const link = option?.link;

    setUserAnswers(prev => {
      const existing = prev[questionId] || { optionIds: [], severities: [], links: [] };
     
      console.log("=========option=index==", questionType)
      
      if (questionType === 101) { // Multiple choice - keep as arrays
        const optionIndex = existing.optionIds.indexOf(optionId);
        console.log("=========option=index==", optionIndex)
        if (optionIndex > -1) {
          return {
            ...prev,
            [questionId]: {
              optionIds: existing.optionIds.filter((_, i) => i !== optionIndex),
              severities: existing.severities.filter((_, i) => i !== optionIndex),
              links: existing.links.filter((_, i) => i !== optionIndex)
            }
          };
        } else {
          return {
            ...prev,
            [questionId]: {
              optionIds: [...existing.optionIds, optionId],
              severities: [...existing.severities, severity],
              links: [...(existing.links || []), link]
            }
          };
        }
      } else { // Single choice - store as arrays for consistency, but will extract single values when sending
        return {
          ...prev,
          [questionId]: {
            optionIds: [optionId],
            severities: [severity],
            links: [link]
          }
        };
      }
    });
  };

  const handleNextFromQuestions = async () => {
    const allSelectedSeverities = Object.values(userAnswers).flatMap(({ severities }) => severities);
    const finalSeverity = calculateSeverity(allSelectedSeverities);
    setCalculatedSeverity(finalSeverity);

    try {
      setIsLoading(true);
      const response = await axios.post("/vehicle-hospital", {
        startLat: startPlaceLocation.current?.lat || 8.9806,
        startLng: startPlaceLocation.current?.lng || 38.7578,
        phone,
        finalSeverity,
        userAnswers
      });

      if (response.data.hospitals?.length > 0) {
        setSuggestedDestination(response.data.hospitals[0]);
      }
      if (response.data.vehicleTypes) {
        // setRecommendedVehicle(response.data.vehicleTypes);
        setSelectedVehicleType(response.data.vehicleTypes.id);
        setSelectedVehicleTypeUUID(response.data.vehicleTypes.uuid);
      }
      setFinalLinks(response.data.finalLink);
    } catch (error) {
      console.error("Error fetching recommendations:", error);
    } finally {
      setIsLoading(false);
      setStep(3);
    }
  };

  const handleDestinationChange = async (value) => {
    setDestinationError("");
    
    // Only proceed if a place was actually selected (not just typed)
    if (!value || !value.label) {
      setDestination(null);
      setDestinationLocation(null);
      return;
    }

    try {
      const results = await geocodeByAddress(value.label);
      const latLng = await getLatLng(results[0]);
      
      // Store destination as an object with name and coordinates
      const destinationData = {
        name: value.label,
        lat: latLng.lat,
        lng: latLng.lng
      };
      
      setDestination(destinationData);
      setDestinationLocation(latLng);
      
      // Don't emit socket event here - wait for user to confirm and proceed
      // This prevents premature request submission when user is just searching
      
    } catch (err) {
      console.error("Geocoding error:", err);
      setDestinationError("Failed to locate address");
      setDestination(null);
      setDestinationLocation(null);
    }
  };


  const handleAcceptSuggestedDestination = () => {
    if (suggestedDestination) {
      const latLng = {
        lat: parseFloat(suggestedDestination.latitude),
        lng: parseFloat(suggestedDestination.longitude)
      };
      
      // Store destination as an object with name and coordinates
      const destinationData = {
        name: suggestedDestination.name,
        lat: latLng.lat,
        lng: latLng.lng
      };
      
      setDestination(destinationData);
      setDestinationLocation(latLng);
      setMapKey(prev => prev + 1);

      const filteredUserAnswers = getFilteredUserAnswers(userAnswers);
      const requestData = {
        startPoint,
        startLat: startPlaceLocation.current?.lat || 8.9806,
        startLng: startPlaceLocation.current?.lng || 38.7578,
        phone,
        is_english_speaker: isEnglishSpeaker,
        userAnswers: filteredUserAnswers,
        finalSeverity: calculatedSeverity,
        destination: suggestedDestination.name,
        destinationLat: latLng.lat,
        destinationLng: latLng.lng,
        first_aid_guide_id: finalLinks
      };


     
  
      setEmergencyRequestData(requestData);
  console.log("=============requestData==========",requestData);
      // Emit socket event
      socket.emit("requestEmergency", requestData);
      setIsLoading(true);      

      // socket.emit("destinationConfirm", requestData);
      setStep(4);
    }
  };

  const handleSubmitAll = (paymentMethod = 'Cash') => {
    setSubmitError("");
    if (!startPoint) {
      setLocationError("Location is required");
      setStep(1);
      return;
    }
    if (!destination) {
      setDestinationError("Destination is required");
      setStep(3);
      return;
    }

    const getStoredUserId = () => {
      const userRaw = localStorage.getItem("userData");
      if (userRaw) {
        try {
          const user = JSON.parse(userRaw);
          if (user?.id != null) return user.id;
        } catch (e) {}
      }

      const passengerRaw = localStorage.getItem("passengerData");
      if (passengerRaw) {
        try {
          const passenger = JSON.parse(passengerRaw);
          if (passenger?.id != null) return passenger.id;
        } catch (e) {}
      }

      return null;
    };

    const userId = getStoredUserId();
    if (!userId) {
      setSubmitError("Your session has expired. Please login again.");
      return;
    }
  
    const allSelectedSeverities = Object.values(userAnswers).flatMap(({ severities }) => severities);
    const finalSeverity = calculateSeverity(allSelectedSeverities);
    setCalculatedSeverity(finalSeverity);
  
    
  
    const requestData = {
      startPoint,
      startLat: startPlaceLocation.current?.lat || 8.9806,
      startLng: startPlaceLocation.current?.lng || 38.7578,
      phone,
      is_english_speaker: isEnglishSpeaker,
      destination,
      destinationLat: destinationLocation?.lat || 8.9806,
      destinationLng: destinationLocation?.lng || 38.7578,
      userAnswers,
      finalSeverity,
      selectedVehicleType,
      paymentMethod
    };
  
    console.log("=============selectedVehicleType==========",selectedVehicleType);
    console.log("=============paymentMethod==========", paymentMethod);
    socket.emit('selectVehicle', {
      requestIds: requestId, // Send the entire requestIds array
      selectedVehicleTypeId: selectedVehicleType,
      userId,
      paymentMethod: paymentMethod
    });
  
    setDriverStatus("LOADING");
    setStep(5);
  };

  const handleBackToChoice = () => {
    setStep(1);
    setStartPoint("");
    setLocationError("");
    setPhone("");
    setPhoneError("");
    setIsEnglishSpeaker(false);
    setUserAnswers({});
    setDestination("");
    setDestinationLocation(null);
    setDestinationError("");
    setSuggestedDestination(null);
    setVehicleTypes([]);
    setAlternatives([]);
    setSelectedVehicleType(null);
    setSelectedVehicleTypeUUID(null);
    setRecommendedVehicle(null);
    setCalculatedSeverity(null);
    setFinalLinks(null);
    setMapKey(0);
    setDriverStatus("LOADING");
    setAcceptedDrivers([]);
  };

  // Enhanced back navigation that preserves data
  const handleBackToStep = (targetStep) => {
    // Only allow going back to previous steps
    if (targetStep < step && isStepAllowed(targetStep)) {
      setStep(targetStep);
      // Clear any errors from the current step
      setLocationError("");
      setPhoneError("");
      setDestinationError("");
    }
  };

  // Function to check if user can edit a specific step
  const canEditStep = (stepNumber) => {
    switch (stepNumber) {
      case 1: // Location step - always editable
        return true;
      case 2: // Questions step - editable if location is valid
        return startPoint && phone && !locationError && !phoneError;
      case 3: // Destination step - editable if questions are answered
        return Object.keys(userAnswers).length > 0;
      case 4: // Vehicle step - editable if destination is set
        return destination && destinationLocation;
      case 5: // Summary step - editable if vehicle is selected
        return selectedVehicleType;
      default:
        return false;
    }
  };

  // Step navigation functions
  const navigateToStep = (targetStep) => {
    if (isStepAllowed(targetStep)) {
      setStep(targetStep);
    }
  };

  const isStepAllowed = (targetStep) => {
    // Step 1 is always allowed
    if (targetStep === 1) return true;
    
    // Step 2 requires location and phone
    if (targetStep === 2) {
      return startPoint && phone && !locationError && !phoneError;
    }
    
    // Step 3 requires completion of step 2 (questions answered)
    if (targetStep === 3) {
      return targetStep <= step && Object.keys(userAnswers).length > 0;
    }
    
    // Step 4 requires destination to be set
    if (targetStep === 4) {
      return targetStep <= step && destination && destinationLocation;
    }
    
    // Step 5 requires vehicle selection
    if (targetStep === 5) {
      return targetStep <= step && selectedVehicleType;
    }
    
    // Don't allow navigation to steps beyond current progress
    return targetStep <= step;
  };

  // Set default vehicle type to recommended on component load
  useEffect(() => {
    if (recommendedVehicle) {
      setSelectedVehicleType(recommendedVehicle.id);
      setSelectedVehicleTypeUUID(recommendedVehicle.uuid);
    }
  }, [recommendedVehicle]);

  // Utility to filter userAnswers to only include selected options
  function getFilteredUserAnswers(userAnswers) {
    const filtered = {};
    for (const [qid, ans] of Object.entries(userAnswers)) {
      if (ans && Array.isArray(ans.optionIds) && ans.optionIds.length > 0) {
        // For single choice questions (only one option), convert arrays to single values
        if (ans.optionIds.length === 1) {
          filtered[qid] = {
            optionIds: ans.optionIds,
            severities: ans.severities,
            links: ans.links?.[0] || null // Extract single link value
          };
        } else {
          // For multiple choice questions, keep as arrays
          filtered[qid] = {
            optionIds: ans.optionIds,
            severities: ans.severities,
            links: ans.links || []
          };
        }
      }
    }
    return filtered;
  }

  const handleRetryVehicleSearch = () => {
    setNoResults(null);
    setIsLoading(true);
    setRetryCount(prev => prev + 1);
    if (emergencyRequestData) {
      const filteredUserAnswersRetry = getFilteredUserAnswers(userAnswers);
      socket.emit("requestEmergency", {
        ...emergencyRequestData,
        userAnswers: filteredUserAnswersRetry,
        first_aid_guide_id: finalLinks
      });
    } else {
      const filteredUserAnswersRetry = getFilteredUserAnswers(userAnswers);
      const requestData = {
        startPoint,
        startLat: startPlaceLocation.current?.lat || 8.9806,
        startLng: startPlaceLocation.current?.lng || 38.7578,
        phone,
        is_english_speaker: isEnglishSpeaker,
        userAnswers: filteredUserAnswersRetry,
        finalSeverity: calculatedSeverity,
        destination,
        destinationLat: destinationLocation?.lat || 8.9806,
        destinationLng: destinationLocation?.lng || 38.7578,
        first_aid_guide_id: finalLinks
      };
      socket.emit("requestEmergency", requestData);
    }
  };

  return (
    <Container>
      <Grid
            style={{
              display: 'grid',
              gridTemplateColumns: step === 5 ? '1fr' : (window.innerWidth > 1024 ? '1fr 1.8fr' : '1fr'),
              gap: '2rem',
              minHeight: 'calc(100vh - 4rem)',
              width: '100%',
            }}
      >
        <FormSection
          initial="initial"
          animate="animate"
          exit="exit"
          variants={animations.pageTransition}
          style={{
            width: '100%',
            maxWidth: '100%',
            gridColumn: '1 / span 1',
          }}
        >
          <Steps 
            currentStep={step} 
            navigateToStep={navigateToStep}
            canNavigateToStep={isStepAllowed}
          />
          
          <AnimatePresence mode="wait">
            {step === 1 && (
              <LocationStep
                startPoint={startPoint}
                locationError={locationError}
                phone={phone}
                phoneError={phoneError}
                isEnglishSpeaker={isEnglishSpeaker}
                onStartPointChange={handleStartPointChange}
                onPhoneChange={handlePhoneChange}
                onEnglishSpeakerChange={handleEnglishSpeakerChange}
                onNext={handleNextFromLocation}
              />
            )}

            {step === 2 && (
              <QuestionsStep
                criticalIds={criticalIds}
                questions={questions}
                userAnswers={userAnswers}
                onOptionChange={handleOptionChange}
                onPrevious={() => handleBackToStep(1)}
                onNext={handleNextFromQuestions}
                isLoading={isLoading}
              />
            )}

            {step === 3 && (
              <DestinationStep
                calculatedSeverity={calculatedSeverity}
                suggestedDestination={suggestedDestination}
                destination={destination}
                destinationError={destinationError}
                onDestinationChange={handleDestinationChange}
                onAcceptSuggestion={handleAcceptSuggestedDestination}
                onPrevious={() => handleBackToStep(2)}
                onNext={() => {
                  // Emit socket event when proceeding with selected destination
                  if (destination && destination.name && destination.lat && destination.lng) {
                    // Clear previous results and set loading state
                    setRecommendedVehicle(null);
                    setAlternatives([]);
                    setNoResults(null);
                    setIsLoading(true);
                    
                    const filteredUserAnswers = getFilteredUserAnswers(userAnswers);
                    const requestData = {
                      startPoint,
                      startLat: startPlaceLocation.current?.lat || 8.9806,
                      startLng: startPlaceLocation.current?.lng || 38.7578,
                      phone,
                      is_english_speaker: isEnglishSpeaker,
                      userAnswers: filteredUserAnswers,
                      finalSeverity: calculatedSeverity,
                      destination: destination.name,
                      destinationLat: destination.lat,
                      destinationLng: destination.lng,
                      first_aid_guide_id: finalLinks
                    };
                    
                    setEmergencyRequestData(requestData);
                    console.log("=============requestData==========", requestData);
                    socket.emit("requestEmergency", requestData);
                  }
                  setStep(4);
                }}
              />
            )}

            {step === 4 && (
              <VehicleStep
                recommendedVehicle={recommendedVehicle}
                vehicleTypes={alternatives.length > 0 ? alternatives : vehicleTypes}
                selectedVehicleType={selectedVehicleType}
                selectedVehicleTypeUUId={selectedVehicleTypeUUId}
                onVehicleSelect={(uuid, id) => { setSelectedVehicleType(id); setSelectedVehicleTypeUUID(uuid); }}
                onPrevious={() => handleBackToStep(3)}
                onSubmit={handleSubmitAll}
                noResults={noResults}
                onRetry={handleRetryVehicleSearch}
                isLoading={isLoading}
                retryCount={retryCount}
              />
            )}

             {step === 5 && (
              <SummaryStep
                calculatedSeverity={calculatedSeverity}
                finalLinks={finalLinks}
                driverStatus={driverStatus}
                setDriverStatus={setDriverStatus}
                acceptedDrivers={acceptedDrivers}
                setAcceptedDrivers={setAcceptedDrivers}
                onBackToChoice={handleBackToChoice}
                onEditStep={handleBackToStep}
                canEditStep={canEditStep}
                socket={socket}
                requestData={emergencyRequestData}
                formData={{
                  startPoint,
                  phone,
                  isEnglishSpeaker,
                  userAnswers,
                  destination,
                  selectedVehicleType,
                  calculatedSeverity
                }}
              />
            )}
          </AnimatePresence>
        </FormSection>

        {step !== 5 && (
          <MapSection
            initial={animations.mapInitial}
            animate={animations.mapAnimate}
            transition={animations.mapTransition}
          >
            <MapContainer
              key={mapKey}
              startPoint={startPlaceLocation.current?.lat ? {
                lat: parseFloat(startPlaceLocation.current.lat),
                lng: parseFloat(startPlaceLocation.current.lng)
              } : null}
              destination={destinationLocation?.lat ? {
                lat: parseFloat(destinationLocation.lat),
                lng: parseFloat(destinationLocation.lng)
              } : null}
            />
          </MapSection>
        )}
      </Grid>
    </Container>
  );
};

export default AmbulanceFlow;